/* eslint-disable ember/no-computed-properties-in-native-classes, ember/classic-decorator-no-classic-methods */
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import dayjs from 'dayjs';
import { timeout } from 'ember-concurrency';
import type { Hotel, Market } from 'frontend-utils/models';
import { b64EncodeUnicode } from 'frontend-utils/utils/base64-encode-decode';
import { DOMAIN_NAME } from 'frontend-utils/utils/constants/domain-utils';
import {
  isPlatformLocalStagingDomain,
  isPlatformProductionDomain,
  isPlatformStagingOrFeatureBranchDomain,
  PLATFORM_PRODUCTION_DOMAIN,
  PLATFORM_STAGING_DOMAIN,
} from 'frontend-utils/utils/domain-checks';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import Cookies from 'js-cookie';
import type { SmartInsightDay } from 'lumen/models';
import {
  SMART_INSIGHT_PRIORITIES,
  type SmartInsightPriority,
} from 'lumen/models/smart-insights/smart-insight-day';
import {
  BI_UPSELL_CONTACT_LOCALSTORAGE_KEY,
  DRAWER_IDS,
  MI_UPSELL_CONTACT_LOCALSTORAGE_KEY,
} from 'lumen/utils/constants/general-constants';
import {
  SMART_INSIGHTS_KPI_COMPARISON_PERIODS,
  SMART_INSIGHTS_TABS,
  SMART_INSIGHTS_VIEW_LOCAL_STORAGE_KEY,
  SMART_INSIGHTS_VIEWS,
  type SmartInsightsKpiComparisonPeriod,
  type SmartInsightsTab,
  type SmartInsightsView,
} from 'lumen/utils/constants/smart-insights';
import {
  TRIAL_INDICATION_LOCALSTORAGE,
  TRIAL_INDICATION_LOCALSTORAGE_EXPIRED_AUTO_OPEN,
  TRIAL_INDICATION_LOCALSTORAGE_KEY,
  TRIAL_INDICATION_LOCALSTORAGE_NEARLY_EXPIRED_AUTO_OPEN,
} from 'lumen/utils/constants/trial-indication-constants';

type Option = {
  action: string;
  icon?: IconName;
  label: string;
};

export default class HotelController extends Controller {
  queryParams = [
    'smartInsightsDay',
    'smartInsightsExpanded',
    'smartInsightsKpiComparisonPeriod',
    'smartInsightsPriorities',
    'smartInsightsTab',
    'smartInsightsView',
  ];

  @service declare access: Services['access'];
  @service declare ajax: Services['ajax'];
  @service declare drawer: Services['drawer'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intercom: Services['intercom'];
  @service declare intl: Services['intl'];
  @service declare localStorage: Services['local-storage'];
  @service declare navigation: Services['navigation'];
  // eslint-disable-next-line ember/no-unused-services
  @service declare roiTrophies: Services['roi-trophies'];
  @service declare router: Services['router'];
  @service declare smartInsights: Services['smart-insights'];

  @tracked smartInsightsDay: string | null = null;
  @tracked smartInsightsExpanded: boolean = false;
  @tracked smartInsightsKpiComparisonPeriod: SmartInsightsKpiComparisonPeriod =
    SMART_INSIGHTS_KPI_COMPARISON_PERIODS.YESTERDAY;
  @tracked smartInsightsPriorities: string = [
    SMART_INSIGHT_PRIORITIES.HIGH,
    SMART_INSIGHT_PRIORITIES.MEDIUM,
    SMART_INSIGHT_PRIORITIES.LOW,
  ].join(',');
  @tracked smartInsightsTab: SmartInsightsTab = SMART_INSIGHTS_TABS.ACTIVE;
  @tracked smartInsightsView: SmartInsightsView = SMART_INSIGHTS_VIEWS.LIST;

  active = false;
  currentHotelId = null;

  get hasStaffAccessIndicator(): boolean {
    if (this.isDemoMode) {
      return false;
    }

    // When Unified Hotel View is on, subscriptions are loaded into navigation
    // so we can check across all of them. Otherwise fall back to the active
    // subscription via the access service.
    if (this.navigation.hasUnifiedHotelView) {
      return this.navigation.hasAnyRestrictedOrDeniedFeatures;
    }

    return this.access.hasRestrictedFeatures || this.access.hasDeniedFeatures;
  }

  get hostDomain(): string {
    return this.ajax.host!;
  }

  @computed('hotelAndMarketService.hotelOrMarket')
  get hotelOrMarket(): Hotel | Market {
    return this.hotelAndMarketService.hotelOrMarket;
  }

  get isAccountButtonActive(): boolean {
    return this.router.currentRouteName === 'hotel.revpak-exports';
  }

  get isDemoMode(): boolean {
    return Boolean(Cookies.get('demoModev3'));
  }

  get isHappyHolidays(): boolean {
    const weekOfYear = dayjs().week();

    return weekOfYear === 51 || weekOfYear === 52;
  }

  get isSettingsButtonActive(): boolean {
    return (
      this.router.currentRouteName!.includes('settings') &&
      !this.isAccountButtonActive
    );
  }

  get priorities(): SmartInsightPriority[] {
    if (!this.smartInsightsPriorities) {
      return [];
    }
    return this.smartInsightsPriorities.split(',') as SmartInsightPriority[];
  }

  get selectedSmartInsight(): SmartInsightDay | null {
    if (!this.smartInsightsDay) {
      return null;
    }

    return (
      this.smartInsights.smartInsightDays.find(
        (day) => day.stayDate === this.smartInsightsDay,
      ) ?? null
    );
  }

  get settingsOptions(): Option[] {
    const options: Option[] = [
      {
        label: this.intl.t('menu.my_account'),
        action: 'redirectToMyAccount',
        icon: 'circle-user',
      },
      {
        label: this.intl.t('menu.help'),
        action: 'openHelpCenter',
        icon: 'circle-question-mark',
      },
    ];

    if (
      this.hotelAndMarketService.hotel &&
      this.access.hasSpiderRevpakLibrary
    ) {
      options.splice(1, 0, {
        label: this.intl.t('menu.spider.revpak-library'),
        action: 'redirectToSpiderHotelRevpak',
        icon: 'bookmark-filled',
      });
    }

    // Insert refer a friend link at 3rd position, in case of no rebranding
    if (!this.hotelOrMarket.hasRebranding) {
      options.splice(2, 0, {
        label: this.intl.t('menu.refer_a_friend'),
        action: 'redirectReferAFriend',
        icon: 'refer-friend',
      });
    }

    options.push({
      label: this.intl.t('menu.logout'),
      action: 'logout',
      icon: 'logout',
    });

    if (this.access.isStaffUser) {
      options.push({
        label: this.intl.t('menu.admin'),
        action: 'redirectToAdmin',
      });
      options.push({
        label: this.intl.t('menu.admin_subscription'),
        action: 'redirectToSubscription',
      });
    }

    if (this.access.hasFeature('user.app_demo_mode')) {
      if (this.isDemoMode) {
        options.push({
          label: this.intl.t('menu.disable_demo'),
          action: 'disableDemo',
        });
      } else {
        options.push({
          label: this.intl.t('menu.enable_demo'),
          action: 'enableDemo',
        });
      }
    }

    return options;
  }

  get showEmptyState(): boolean {
    const isSettingsPage = this.router.currentRouteName!.includes('.settings.');

    return this.access.isContextHiddenForStaffUsers && !isSettingsPage;
  }

  get showSmartInsights(): boolean {
    return (
      this.router.currentRouteName!.startsWith('hotel.') &&
      this.access.hasSmartInsights
    );
  }

  constructor() {
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    this.drawer.registerDrawer(DRAWER_IDS.SMART_INSIGHTS_DRAWER);
  }

  @action
  disableDemo(): void {
    Cookies.remove('demoModev3');
    window.location.reload();
  }

  @action
  enableDemo(): void {
    Cookies.set(
      'demoModev3',
      b64EncodeUnicode(
        JSON.stringify({
          active: true,
          map: [],
          hotelinfo: this.hotelAndMarketService.hotel?.hotelinfo?.name,
        }),
      ),
    );
    window.location.reload();
  }

  @action
  logout(): void {
    // Segment stores personal info in localstorage
    // Remove it before logging out
    this.localStorage.removeItem('ajs_user_traits');
    this.localStorage.removeItem(TRIAL_INDICATION_LOCALSTORAGE);
    this.localStorage.removeItem(TRIAL_INDICATION_LOCALSTORAGE_KEY);
    this.localStorage.removeItem(
      TRIAL_INDICATION_LOCALSTORAGE_NEARLY_EXPIRED_AUTO_OPEN,
    );
    this.localStorage.removeItem(
      TRIAL_INDICATION_LOCALSTORAGE_EXPIRED_AUTO_OPEN,
    );
    this.localStorage.removeItem(BI_UPSELL_CONTACT_LOCALSTORAGE_KEY);
    this.localStorage.removeItem(MI_UPSELL_CONTACT_LOCALSTORAGE_KEY);

    // Clear firebase auth data
    indexedDB.deleteDatabase('firebaseLocalStorageDb');

    this.ajax
      .post('/logout/')
      .then(() => {
        window.location.href = '/login';
      })
      .catch(() => {
        window.location.href = '/login';
      });
  }

  @action
  openHelpCenter(): void {
    this.intercom.redirectToIntercomHelp();
  }

  @action
  performAction(option: Option): void {
    this.send(option.action);
  }

  @action
  redirectReferAFriend(): void {
    const extraData = [
      {
        name: 'referrer_first_name',
        value: this.hotelAndMarketService.user?.firstName,
      },
      {
        name: 'referrer_last_name',
        value: this.hotelAndMarketService.user?.lastName,
      },
      {
        name: 'referrer_email',
        value: this.hotelAndMarketService.user?.email,
      },
      {
        name: 'referrer_email',
        value: this.hotelAndMarketService.user?.email,
      },
      {
        name: 'referrer_property_name',
        value: this.hotelAndMarketService.hotelOrMarket?.hotelinfo?.name,
      },
    ];

    // Refer a friend menu item is not visible for resellers, so no extra checks required here
    const baseUrl = `https://www.${DOMAIN_NAME}/lp/refer-a-friend`;

    // form a string containing all necessary URL parameters
    let urlParams = '?';

    extraData.forEach((param, index) => {
      urlParams += `${param.name}=${encodeURIComponent(param.value)}`;

      if (index !== extraData.length - 1) {
        urlParams += '&';
      }
    });

    openUrl(`${baseUrl}${urlParams}`);
  }

  @action
  redirectToAdmin(): void {
    let url;

    // Admin only feature, so no further domain checks necessary here
    if (isPlatformProductionDomain()) {
      url = `https://${PLATFORM_PRODUCTION_DOMAIN}/admin/hotel_industry/hotelinfo/${this.hotelAndMarketService.hotel?.hotelinfo?.id}/`;
    } else if (isPlatformStagingOrFeatureBranchDomain()) {
      url = `https://${window.location.hostname}/admin/hotel_industry/hotelinfo/${this.hotelAndMarketService.hotel?.hotelinfo?.id}/`;
    } else if (isPlatformLocalStagingDomain()) {
      url = `https://${PLATFORM_STAGING_DOMAIN}/admin/hotel_industry/hotelinfo/${this.hotelAndMarketService.hotel?.hotelinfo?.id}/`;
    }

    openUrl(url);
  }

  @action
  redirectToMyAccount(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo('hotel.settings.personal');
  }

  @action
  redirectToSettings(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo('hotel.settings');
  }

  @action
  redirectToSpiderHotelRevpak(): void {
    this.router.transitionTo(
      'hotel.revpak-exports',
      this.hotelAndMarketService.hotel!.id,
    );
  }

  @action
  redirectToSubscription(): void {
    let url;

    // Admin only feature, so no further domain checks necessary here
    if (isPlatformProductionDomain()) {
      url = `https://${PLATFORM_PRODUCTION_DOMAIN}/admin/subscriptions/subscription/${this.hotelAndMarketService.hotel?.id}/change/`;
    } else if (isPlatformStagingOrFeatureBranchDomain()) {
      url = `https://${window.location.hostname}/admin/subscriptions/subscription/${this.hotelAndMarketService.hotel?.id}/change/`;
    } else if (isPlatformLocalStagingDomain()) {
      url = `https://${PLATFORM_STAGING_DOMAIN}/admin/hotel_industry/hotelinfo/${this.hotelAndMarketService.hotel?.hotelinfo?.id}/`;
    }

    openUrl(url);
  }

  @action
  redirectToTripadvisorManagementConsole(): void {
    window.parent.postMessage(
      {
        action: 'redirect-to-management-center',
      },
      '*',
    );
  }

  @action
  setSmartInsightsDay(day: string | null): void {
    this.smartInsightsDay = day;
  }

  @action
  setSmartInsightsKpiComparisonPeriod(
    period: SmartInsightsKpiComparisonPeriod,
  ): void {
    this.smartInsightsKpiComparisonPeriod = period;
  }

  @action
  setSmartInsightsPriorities(priorities: SmartInsightPriority[]): void {
    this.smartInsightsPriorities = priorities.join(',');
  }

  @action
  setSmartInsightsTab(tab: SmartInsightsTab): void {
    this.smartInsightsTab = tab;
  }

  @action
  setSmartInsightsView(view: SmartInsightsView): void {
    this.smartInsightsView = view;

    this.localStorage.setItem(SMART_INSIGHTS_VIEW_LOCAL_STORAGE_KEY, view);
  }

  @action
  toggleActive(): void {
    this.toggleProperty('active');
  }

  @action
  async toggleSmartInsightsDrawer(): Promise<void> {
    this.drawer.toggleDrawerState(DRAWER_IDS.SMART_INSIGHTS_DRAWER);
    this.smartInsightsExpanded =
      this.drawer.isDrawerExpanded(DRAWER_IDS.SMART_INSIGHTS_DRAWER) ?? false;

    // Trigger window resize after the drawer's 500ms CSS transition so charts
    // (Highcharts) reflow to the new container width.
    await timeout(500);
    window.dispatchEvent(new Event('resize'));
  }
}
